import type { FocusEvent, MouseEvent, ReactElement, ReactNode } from 'react';
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { browserOnly } from '@farmersdog/utils';
import classNames from 'classnames';

import styles from './Tooltip.module.css';

import { Text } from '../Text';
import { getMaxTooltipWidth } from './utils/getMaxTooltipWidth';

export type TooltipPosition =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'right'
  | 'right-top'
  | 'right-bottom'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'left'
  | 'left-top'
  | 'left-bottom';

export type TooltipMode = 'light' | 'dark';

export type TooltipChild = ReactElement<{
  onMouseEnter?: (e: MouseEvent) => void;
  onMouseLeave?: (e: MouseEvent) => void;
  onFocus?: (e: FocusEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  onClick?: (e: MouseEvent) => void;
  children?: ReactElement | ReactElement[];
  'aria-describedby': string;
}>;

export interface TooltipProps {
  children: TooltipChild;
  position: TooltipPosition;
  mode?: TooltipMode;
  content?: ReactNode;
  className?: string;
  id?: string;
}

/**
 * Renders a tooltip.
 *
 * @see https://corgi-x.tfd.engineering/components/tooltip
 */
export function Tooltip({
  children,
  position,
  content,
  mode = 'light',
  className,
  ...props
}: TooltipProps): JSX.Element {
  const el = useRef<HTMLSpanElement>(null);
  const tooltipWrapperRef = useRef<HTMLSpanElement>(null);

  const reactId = useId();
  const id = props.id || reactId;

  const [isHidden, setIsHidden] = useState(true);
  const [tooltipWidth, setTooltipWidth] = useState<number | null>(null);

  const hide = (): void => {
    setIsHidden(true);
  };

  const show = (): void => {
    setIsHidden(false);
  };

  // Special case - prevent mouseleave hiding tooltip if element in focus using
  // document.contains because child element could have multiple focusable
  // children
  const handleMouseLeave = (): void => {
    if (el.current && !el.current.contains(document.activeElement)) {
      setIsHidden(true);
    }
  };

  const handleBlur = (): void => {
    browserOnly(window => {
      // This pushes the hide to the end of the event loop ensuring that on
      // touch devices they can interact with the tooltip content before it
      // hides
      window.setTimeout(hide, 0);
    });
  };

  useEffect(() => {
    return browserOnly((_, document) => {
      const handleKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
          setIsHidden(true);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return (): void => document.removeEventListener('keydown', handleKeyDown);
    });
  }, []);

  useEffect(() => {
    if (!tooltipWrapperRef.current || isHidden) {
      setTooltipWidth(null);
      return;
    }
    setTooltipWidth(getMaxTooltipWidth(tooltipWrapperRef.current));
  }, [isHidden]);

  if (!isValidElement(children)) {
    throw new Error(
      'Tooltip only accepts a single child as a valid trigger element'
    );
  }

  const triggerElement = Children.map(children, (child: TooltipChild) =>
    cloneElement(child, {
      'aria-describedby': id,
      onMouseEnter: (e: MouseEvent) => {
        show();
        child.props.onMouseEnter?.(e);
      },
      onMouseLeave: (e: MouseEvent) => {
        handleMouseLeave();
        child.props.onMouseLeave?.(e);
      },
      onFocus: (e: FocusEvent) => {
        show();
        child.props.onFocus?.(e);
      },
      onBlur: (e: FocusEvent) => {
        handleBlur();
        child.props.onFocus?.(e);
      },
      // We need to attach onClick to support iOS Safari
      onClick: (e: MouseEvent) => {
        show();
        child.props.onClick?.(e);
      },
    })
  );

  return (
    <span
      className={classNames(styles['overlay-wrapper'], className)}
      aria-label="Toggle tooltip"
      ref={el}
      {...props}
    >
      {triggerElement}
      <span
        ref={tooltipWrapperRef}
        className={classNames(
          styles['tooltip-wrapper'],
          styles[`tooltip-${position}`]
        )}
        onMouseEnter={show}
        onMouseLeave={hide}
        aria-hidden={content ? isHidden : true}
        style={
          tooltipWidth !== null
            ? { width: `${tooltipWidth}px`, maxWidth: `${tooltipWidth}px` }
            : undefined
        }
      >
        <span
          className={classNames(styles.tooltip, styles[mode])}
          role="tooltip"
          id={id}
        >
          {typeof content === 'string' ? <Text>{content}</Text> : content}
        </span>
      </span>
    </span>
  );
}
